import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Youroheader from "../Youro-header";
import Loader from "../../utils/loader";
import Popup from 'reactjs-popup';
import { ToastContainer, toast } from 'react-toastify';
import { COOKIE_KEYS, USER_TYPES, API_DETAILS} from "../../App";
import Cookies from "js-cookie";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { formatDate } from "../../utils/formatDate";

const PatientPrescription = (props) => {
    const navigate = useNavigate();
    const [viewVal, setViewVal] = useState(0);
    const [activeLoader, setActiveLoader] = useState(false);
    // Refills need an active membership, so the page has to know before the
    // patient tries. Assume active until told otherwise, so a slow or failed
    // lookup never wrongly accuses someone of having lapsed.
    const [subscriptionActive, setSubscriptionActive] = useState(true);
    const [isOpenCreatePopUp, setOpenCreatePopUp] = useState(false);
    const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
    const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
    const [data, setData] = useState([]);
    const userName = Cookies.get(COOKIE_KEYS.userName);
    const prescriptionRef = useRef();
    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
    });
    const [allMedications, setAllMedications] = useState([]);
    const [filteredMedications, setFilteredMedications] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('all');
    const [loadingCount, setLoadingCount] = useState(0);

    const startLoading = () => setLoadingCount((count) => count + 1);
    const stopLoading = () => setLoadingCount((count) => Math.max(count - 1, 0));
    const isLoading = loadingCount > 0;

    useEffect(() => {
      setActiveLoader(isLoading);
    }, [isLoading]);

    const fetchSubscriptionStatus = async () => {
      const uId = Cookies.get(COOKIE_KEYS.userId);
      if (!uId) return;
      const url =
        API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
        `/getSubscriptionDetails/${uId}`;
      try {
        const res = await axios.get(url);
        setSubscriptionActive(res.data?.isActive !== false);
      } catch (err) {
        console.error("Error fetching subscription details:", err);
      }
    };

    useEffect(() => {
      const fetch = async () => {
        startLoading();
        try {
          await fetchData();
          await fetchSubscriptionStatus();
        } finally {
          stopLoading();
        }
      };

      fetch();
    }, []);

    useEffect(() => {
      const fetch = async () => {
        startLoading();
        try {
          await fetchProfileData();
          if (viewVal === 4 || viewVal === 6) {
            navToProfile(viewVal);
          }
        } finally {
          stopLoading();
        }
      };

      fetch();
    }, [viewVal]);

    useEffect(() => {
        if (data && data.length > 0) {
            const medications = [];
            const dates = new Set();

            data.forEach((prescription) => {
                if (prescription.carePlan && prescription.carePlan.presTypes) {
                    Object.values(prescription.carePlan.presTypes).forEach((prescriptionArray) => {
                        prescriptionArray.forEach((item) => {
                            if (item.medication === true) {
                                const medItem = {
                                    ...item,
                                    diagName: prescription.diagName,
                                    doctorName: prescription.doctorName,
                                    prescribedDate: prescription.lastModified,
                                    apptId: prescription.apptId,
                                    docId: prescription.docId
                                };
                                medications.push(medItem);

                                // Add date to available dates
                                dates.add(prescription.lastModified);
                            }
                        });
                    });
                }
            });

            medications.sort((a, b) => {
                const dateA = new Date(a.prescribedDate);
                const dateB = new Date(b.prescribedDate);
                return dateB - dateA;
            });

            const sortedDates = Array.from(dates).sort((a, b) => {
                return new Date(b) - new Date(a);
            });

            setAllMedications(medications);
            setFilteredMedications(medications);
            setAvailableDates(sortedDates);

            if (sortedDates.length > 0) {
                setSelectedDate(sortedDates[0]);
            }
        }
    }, [data]);

    useEffect(() => {
        if (selectedDate === 'all') {
            setFilteredMedications(allMedications);
        } else {
            const filtered = allMedications.filter(med => med.prescribedDate === selectedDate);
            setFilteredMedications(filtered);
        }
    }, [selectedDate, allMedications]);

    const navToProfile = (viewVal) => {
        props.changeView(viewVal);
    };

    // Kept as a thin alias so the call sites below read unchanged; the shared
    // helper gives MM/DD/YYYY and tolerates a missing date, which the old
    // split-on-dash version threw on.
    const convertDateFormat = (date) => formatDate(date, '');

    const extractPrescriptionDate = (lastModified, includeTime = false) => {
        if (!lastModified) return '--';
        const date = new Date(lastModified);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        const formattedDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        return includeTime ? `${formattedDate} ${formattedTime}` : formattedDate;
    };

    const fetchProfileData = async () => {
        const uId = Cookies.get(COOKIE_KEYS.userId);
        const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/getUser/${uId}`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };

        try {
            const res = await axios.get(url, config);
            console.log(res.data);
            setUserData({
                firstName: res.data.firstName,
                lastName: res.data.lastName,
                dateOfBirth: convertDateFormat(res.data.dateOfBirth),
                address: res.data.address,
                city: res.data.city,
                state: res.data.state,
                zipCode: res.data.zipCode
            });
        }
        catch (err) {
            console.error(err);
        }
    };

    const fetchData = async () => {
        const patientId = Cookies.get(COOKIE_KEYS.userId);
        const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/getAllPrescriptionByPatient?patientId=${patientId}`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };

        try {
            const res = await axios.get(url, config);
            console.log('prescription data', res.data);

            const sortedData = res.data.sort((a, b) => {
                const dateA = new Date(a.lastModified);
                const dateB = new Date(b.lastModified);
                return dateB - dateA;
            });

            setData(sortedData);
        } catch (err) {
            console.error(err);
        }
    };

    const extractInstructionsFromOverview = (overview) => {
        if (!overview) {
            return 'Take as directed by your physician.';
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = overview;

        const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
        let instructionsText = [];
        let inInstructionsSection = false;

        const majorSectionHeaders = [
            'explanation:',
            'possible side effects:',
            'warnings:',
            'contraindications:',
            'interactions:',
            'precautions:',
            'how to take:',
            'storage:'
        ];

        for (let i = 0; i < paragraphs.length; i++) {
            const p = paragraphs[i];
            const text = p.textContent.trim();

            if (!text || text === '&nbsp;' || text === '') continue;

            const strongTag = p.querySelector('strong');

            if (strongTag) {
                const strongText = strongTag.textContent.trim().toLowerCase();

                if (strongText === 'instructions:') {
                    inInstructionsSection = true;
                    continue;
                }

                if (inInstructionsSection && majorSectionHeaders.some(header => strongText === header)) {
                    break;
                }
            }

            if (inInstructionsSection) {
                instructionsText.push(text);
            }
        }

        return instructionsText.length > 0
            ? instructionsText.join(' ')
            : 'Take as directed by your physician.';
    };

    const getMedicationData = (prescription) => {
        if (prescription.medication === true && prescription.presName) {
            const words = prescription.presName.trim().split(' ');
            let medicationName = prescription.presName;
            let parsedDosage = '--';
            let parsedFrequency = '--';

            if (words.length >= 2) {
                parsedFrequency = words[words.length - 1];

                const secondLast = words[words.length - 2];
                if (secondLast.includes('mg') || secondLast.includes('ml') || /\d/.test(secondLast)) {
                    parsedDosage = secondLast;
                    medicationName = words.slice(0, words.length - 2).join(' ');
                } else {
                    parsedDosage = secondLast;
                    medicationName = words.slice(0, words.length - 1).join(' ');
                }
            }

            const instructions = extractInstructionsFromOverview(prescription.overview);

            let dosageDisplay = parsedDosage;
            if (prescription.strength && prescription.strength.trim() !== '') {
                dosageDisplay = prescription.strength;
            }

            let frequencyDisplay = parsedFrequency;
            if (prescription.frequency && prescription.frequency.trim() !== '') {
                frequencyDisplay = prescription.frequency;
            }

            return {
                name: medicationName,
                dosage: dosageDisplay,
                frequency: frequencyDisplay,
                quantity: prescription.dosage || '--',
                instructions: instructions,
                category: prescription.type?.presTypeName || 'Medication',
                refills: prescription.refillCount || 0,
                prescribedBy: prescription.doctorName || 'Unknown',
                prescribedDate: extractPrescriptionDate(prescription.prescribedDate),
                diagName: prescription.diagName,
                status: (prescription.refillCount || 0) > 0 ? 'Active' : 'Refill Needed',
                presId: prescription.presId,
                fullPrescription: prescription
            };
        }
        return null;
    };

    const filterPrescriptionRefill = () => {
        const oneYear = new Date();
        oneYear.setFullYear(oneYear.getFullYear() - 1);

        const filtered = [];
        const seenPresNames = new Set();
        data.forEach((item) => {
          const lastModifiedDate = new Date(item.lastModified);
          if (lastModifiedDate >= oneYear && item.carePlan?.presTypes) {
            const presTypes = item.carePlan.presTypes;

            Object.entries(presTypes).forEach(([presTypeId, prescriptionList]) => {
              prescriptionList.forEach((prescription) => {
                if (
                  prescription.medication === true &&
                  !seenPresNames.has(prescription.presName)
                ) {
                  filtered.push({
                    presId: prescription.presId,
                    carePlanId: prescription.carePlanId,
                    presName: prescription.presName,
                    diagnosis: item.diagName,
                    docId: item.docId,
                    apptId: item.apptId
                  });
                  seenPresNames.add(prescription.presName);
                }
              });
            });
          }
        });

        filtered.sort((a, b) => {
            const dateA = new Date(a.prescribedDate);
            const dateB = new Date(b.prescribedDate);
            return dateB - dateA;
        });

        setFilteredPrescriptions(filtered);
    };

    const handleCheckboxChange = (item) => {
      setSelectedPrescriptions(prevSelected => {
        const isSelected = prevSelected.some(i => i.presId === item.presId);

        if (isSelected) {
          return prevSelected.filter(i => i.presId !== item.presId);
        } else {
          return [...prevSelected, item];
        }
      });
    };

    const handleSubmit = async () => {
      if (selectedPrescriptions.length === 0) {
        toast.error("Please select at least one item");
        return;
      }

      setOpenCreatePopUp(false);
      const uId = Cookies.get(COOKIE_KEYS.userId);
      const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/refillPrescriptions/${uId}`;
      const config = {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': '*',
          'Content-Type': 'application/json'
        }
      };

      setActiveLoader(true);
      try {
        const docPrescriptions = {
          presReq: selectedPrescriptions.reduce((acc, item) => {
            if (!acc[item.docId]) { acc[item.docId] = []; }
            acc[item.docId].push({ presId: item.presId, carePlanId: item.carePlanId });
            return acc;
          }, {}),
          apptId: Math.min(...selectedPrescriptions.map(item => item.apptId))
        };

        await axios.post(url, docPrescriptions, config);
        const updatedData = data.map((prescription) => {
          const newPrescription = { ...prescription };

          if (newPrescription.carePlan && newPrescription.carePlan.presTypes) {
            Object.keys(newPrescription.carePlan.presTypes).forEach((type) => {
              newPrescription.carePlan.presTypes[type] = newPrescription.carePlan.presTypes[type].map((item) => {
                // Check if this item was in our selected refill list
                const isRequested = selectedPrescriptions.some(selected => selected.presId === item.presId);

                if (isRequested) {
                  return {
                    ...item,
                    refillCount: Math.max((item.refillCount || 0) - 1, 0)
                  };
                }
                return item;
              });
            });
          }
          return newPrescription;
        });

        setData(updatedData);
        toast.success("Successfully Requested");
      } catch (err) {
        console.error('Failed to save description:', err);
        toast.error("Error requesting refill: " + err.message);
      } finally {
        setActiveLoader(false);
        setSelectedPrescriptions([]);
      }
    };

    const handlePrint = (medication) => {
        console.log('Print prescription:', medication);
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>Prescription</title>');
        printWindow.document.write('<style>body{font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;padding:20px;}</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h2>${medication.name}</h2>`);
        printWindow.document.write(`<p><strong>Dosage:</strong> ${medication.dosage}</p>`);
        printWindow.document.write(`<p><strong>Frequency:</strong> ${medication.frequency}</p>`);
        printWindow.document.write(`<p><strong>Instructions:</strong> ${medication.instructions}</p>`);
        printWindow.document.write(`<p><strong>Prescribed By:</strong> ${medication.prescribedBy}</p>`);
        printWindow.document.write(`<p><strong>Prescribed Date:</strong> ${medication.prescribedDate}</p>`);
        printWindow.document.write(`<p><strong>Diagnosis:</strong> ${medication.diagName}</p>`);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const handleRefillRequest = (medication) => {
        // Refills are a membership benefit. Stop here rather than opening the
        // request dialog and failing at the end of it.
        if (!subscriptionActive) {
            toast.error("Your membership has expired. Renew to request prescription refills.");
            navigate("/pricing");
            return;
        }

        console.log('Request refill for:', medication);
        filterPrescriptionRefill();

        setTimeout(() => {
            const matchingPres = filteredPrescriptions.find(p => p.presId === medication.presId);
            if (matchingPres) {
                setSelectedPrescriptions([matchingPres]);
            }
            setOpenCreatePopUp(true);
        }, 100);
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };

    const getCurrentDiagnosis = () => {
        if (selectedDate === 'all') return 'All Diagnoses';

        const prescription = data.find(p => p.lastModified === selectedDate);
        return prescription?.diagName || 'N/A';
    };

    return (
        <div style={{ display: 'flex', flexDirection: "column", width: "100%" }} className = "whole-container">
            <ToastContainer />

            {/* Main Content */}
            <main className="flex-1 overflow-auto" style={{ padding: '32px', overflowY: 'auto' }}>
                <div style={{ opacity: 1 }}>
                    <div className="max-w-6xl">
                        <div style={{ opacity: 1, transform: 'none' }}>
                            {/* No back button here by design. Prescriptions is a
                                sidebar destination as well as a profile-menu one,
                                so a back arrow would point somewhere the patient
                                may not have come from. The other profile screens
                                are only reachable from the menu, and keep theirs. */}
                            <h2 style={{ marginBottom: '8px', fontSize: '24px', fontWeight: '600', fontFamily: "'Arial', sans-serif" }}>
                                My Prescriptions
                            </h2>
                            <p style={{ marginBottom: '24px', opacity: 0.7, fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>
                                View and manage your active prescriptions. Present these at your pharmacy for fulfillment.
                            </p>

                            {/* Refills require an active membership. Same prompt and route as
                                the calendar tab, so a lapsed patient meets one consistent path
                                back to payment wherever they hit the limit. */}
                            {!subscriptionActive && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/pricing")}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        marginBottom: 24,
                                        padding: "14px 16px",
                                        borderRadius: 10,
                                        border: "1px solid #FCA5A5",
                                        backgroundColor: "#FEE2E2",
                                        color: "#B91C1C",
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        fontFamily: "'Arial', sans-serif",
                                    }}
                                >
                                    Active subscription needed to request a prescription refill.{" "}
                                    <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                                        Renew now →
                                    </span>
                                </button>
                            )}

                            {/* Date Filter Box */}
                            {availableDates.length > 0 && (
                                <div style={{
                                    marginBottom: '24px',
                                    padding: '20px',
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: '10px',
                                    border: '1px solid #e0e0e0',
                                    fontFamily: "'Arial', sans-serif"
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: '1', minWidth: '250px' }}>
                                            <label htmlFor="dateFilter" style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#424242'
                                            }}>
                                                Filter by Prescription Date:
                                            </label>
                                            <select
                                                id="dateFilter"
                                                value={selectedDate}
                                                onChange={handleDateChange}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    fontSize: '14px',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#ffffff',
                                                    cursor: 'pointer',
                                                    fontFamily: "'Arial', sans-serif"
                                                }}
                                            >
                                                <option value="all">All Dates</option>
                                                {availableDates.map((date, index) => (
                                                    <option key={index} value={date}>
                                                        {extractPrescriptionDate(date, true)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '250px' }}>
                                            <p style={{
                                                margin: '0',
                                                fontSize: '14px',
                                                color: '#666',
                                                marginBottom: '4px'
                                            }}>
                                                <strong>Diagnosis:</strong>
                                            </p>
                                            <p style={{
                                                margin: '0',
                                                fontSize: '16px',
                                                color: '#424242',
                                                fontWeight: '600'
                                            }}>
                                                {getCurrentDiagnosis()}
                                            </p>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* Prescription Cards Container */}
                            <div className="space-y-6" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                {filteredMedications && filteredMedications.length > 0 ? (
                                    filteredMedications.map((medication, index) => {
                                        const medData = getMedicationData(medication);

                                        if (!medData) return null;

                                        return (
                                            <PrescriptionCard
                                                key={`${medication.presId}-${index}`}
                                                medication={medData}
                                                onPrint={() => handlePrint(medData)}
                                                onRefill={() => handleRefillRequest(medData)}
                                            />
                                        );
                                    })
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: '10px',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <p style={{ fontSize: '16px', color: '#666' }}>
                                            No prescription data available for the selected date!
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Refill Request Popup */}
            <Popup
                className='congrats-popup'
                closeOnEscape={false}
                open={isOpenCreatePopUp}
                modal
                closeOnDocumentClick={false}
                onClose={() => {
                    setOpenCreatePopUp(false);
                    setSelectedPrescriptions([]);
                }}
                contentStyle={{ maxWidth: '1200px' }}
            >
                <div
                    style={{
                        top: 0,
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderBottom: '1px solid #ccc',
                        zIndex: 10,
                        textAlign: 'center',
                        position: 'relative',
                    }}
                >
                    <h2 style={{ margin: 0 }}>Medications Prescribed</h2>
                    <div
                        style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer' }}
                        onClick={() => {
                            setOpenCreatePopUp(false);
                            setSelectedPrescriptions([]);
                        }}
                    >
                        <span className="material-symbols-outlined">close</span>
                    </div>
                </div>

              <div style={{ padding: '20px', height: '80vh', overflowY: 'auto' }}>
                {filteredPrescriptions.length === 0 ? (
                  <p>No medications found.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {filteredPrescriptions.map((item) => (
                        <label
                          key={item.presId}
                          htmlFor={`item-${item.presId}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            padding: '10px 15px',
                            backgroundColor: '#f7f7f7',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`item-${item.presId}`}
                            checked={selectedPrescriptions.some(i => i.presId === item.presId)}
                            onChange={() => handleCheckboxChange(item)}
                            style={{ cursor: 'pointer' }}
                          />
                          <div>
                            <strong>{item.presName}</strong>
                            <div style={{ marginTop: '4px' }}>
                              <small>Diagnosis: {item.diagnosis}</small>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div
                    className='submit-button'
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: '#fff',
                        padding: '15px 20px',
                        borderTop: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 10,
                    }}
                >
                    <p style={{ color: 'red', fontSize: '12px', margin: 0, flexBasis: '80%', maxWidth: '80%' }}>
                        *Only medications prescribed in the past 12 months are shown. For older prescriptions, please schedule a new appointment.
                    </p>
                    <button className='btn-filled' onClick={handleSubmit} style={{ padding: '10px 15px', cursor: 'pointer' }} >
                        Request Refill
                    </button>
                </div>
            </Popup>
            <Loader active={activeLoader}/>
        </div>
    );
};

// Prescription Card Component - Same as before
const PrescriptionCard = ({ medication, onPrint, onRefill }) => {
    const isRefillNeeded = medication.status === 'Refill Needed';

    return (
        <div style={{ opacity: 1, transform: 'none' }}>
            <div
                className="bg-card text-card-foreground"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff'
                }}
            >
                {/* Card Header */}
                <div className="card-header" style={{ padding: '24px 24px 0', fontFamily: "'Arial', sans-serif" }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backgroundColor: '#C8E6C9' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#66BB6A' }}>
                                    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                                    <path d="m8.5 8.5 7 7"></path>
                                </svg>
                            </div>

                            <div>
                                <h4 style={{ lineHeight: '1', marginBottom: '8px', fontSize: '18px', fontWeight: '600', fontFamily: "'Arial', sans-serif" }}>
                                    {medication.name}
                                </h4>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid', padding: '2px 8px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap', backgroundColor: isRefillNeeded ? '#FFEBEE' : '#E8F5E9', color: isRefillNeeded ? '#C62828' : '#2E7D32', borderColor: isRefillNeeded ? '#FFCDD2' : '#C8E6C9', fontFamily: "'Arial', sans-serif" }}>
                                        {medication.status}
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #d0d0d0', padding: '2px 8px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap', backgroundColor: '#f5f5f5', color: '#424242', fontFamily: "'Arial', sans-serif" }}>
                                        {medication.category}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button onClick={onPrint} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s', border: 'none', background: 'transparent', height: '32px', borderRadius: '6px', gap: '6px', padding: '0 12px', cursor: 'pointer', color: '#1976D2', flexShrink: 0, fontFamily: "'Arial', sans-serif" }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                                <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                                <rect x="6" y="14" width="12" height="8" rx="1"></rect>
                            </svg>
                            Print
                        </button>
                    </div>
                </div>

                <div className="card-content" style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Arial', sans-serif" }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                            <p style={{ margin: 0, fontSize: '14px', opacity: 0.7, fontFamily: "'Arial', sans-serif" }}>Dosage</p>
                            <p style={{ margin: '4px 0 0', fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>{medication.dosage}</p>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: '14px', opacity: 0.7, fontFamily: "'Arial', sans-serif" }}>Frequency</p>
                            <p style={{ margin: '4px 0 0', fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>{medication.frequency}</p>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: '14px', opacity: 0.7, fontFamily: "'Arial', sans-serif" }}>Prescribed By</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <p style={{ margin: 0, fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>Dr. {medication.prescribedBy}</p>
                            </div>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: '14px', opacity: 0.7, fontFamily: "'Arial', sans-serif" }}>Prescribed Date</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <path d="M8 2v4"></path>
                                    <path d="M16 2v4"></path>
                                    <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                                    <path d="M3 10h18"></path>
                                </svg>
                                <p style={{ margin: 0, fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>{medication.prescribedDate}</p>
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#E9E4D7FF', fontFamily: "'Arial', sans-serif" }}>
                        <p style={{ margin: '0 0 8px', fontSize: '14px', opacity: 0.7, fontFamily: "'Arial', sans-serif" }}>
                            <strong>Instructions:</strong>
                        </p>
                        <p style={{ margin: 0, fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>
                            {medication.instructions}
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid #e0e0e0', flexWrap: 'wrap', gap: '12px', fontFamily: "'Arial', sans-serif" }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                <path d="M3 3v5h5"></path>
                                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                                <path d="M16 16h5v5"></path>
                            </svg>
                            <p style={{ margin: 0, fontSize: '14px', fontFamily: "'Arial', sans-serif" }}>
                                <strong>Refills Remaining:</strong> {medication.refills}
                            </p>
                        </div>
                        <button
                            disabled={isRefillNeeded}
                            onClick={onRefill}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'all 0.2s',
                                border: 'none',
                                backgroundColor: isRefillNeeded ? '#e5e7eb' : 'var(--youro-coral)',
                                color: isRefillNeeded ? '#9ca3af' : '#ffffff',
                                height: '40px',
                                borderRadius: '6px',
                                padding: '0 24px',
                                cursor: isRefillNeeded ? 'not-allowed' : 'pointer',
                                fontFamily: "'Arial', sans-serif",
                                opacity: isRefillNeeded ? 0.8 : 1
                            }}
                            onMouseOver={(e) => {
                                if (!isRefillNeeded) {
                                    e.currentTarget.style.filter = 'brightness(0.9)';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!isRefillNeeded) {
                                    e.currentTarget.style.filter = 'none';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }
                            }}
                        >
                            {isRefillNeeded ? 'See a doctor for refill' : 'Request Refill'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientPrescription;